import { Injectable } from '@angular/core';

/**
 * ThemeService: Handles dark/light mode based on system and user preference.
 * - Uses Tailwind CSS v4+ (class strategy)
 * - Persists user choice in localStorage
 * - Defaults to system preference if no user override
 * - Listens for system preference changes
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'theme';

  constructor() {
    this.initTheme();
    // Listen for system preference changes if user hasn't set a preference
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
      if (!localStorage.getItem(this.storageKey)) {
        this.setTheme(e.matches ? 'dark' : 'light', false);
      }
    });
  }

  /**
   * Initialize theme on app start
   */
  public initTheme() {
    const userPref = localStorage.getItem(this.storageKey);
    if (userPref === 'dark' || userPref === 'light') {
      this.setTheme(userPref as 'dark' | 'light', false);
    } else {
      const systemPref = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      this.setTheme(systemPref, false);
    }
  }

  /**
   * Set theme and optionally persist
   */
  setTheme(theme: 'dark' | 'light', persist = true) {
    if (persist) localStorage.setItem(this.storageKey, theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }

  /**
   * Toggle between dark and light mode
   */
  toggleTheme() {
    const isDark = document.documentElement.classList.contains('dark');
    this.setTheme(isDark ? 'light' : 'dark');
  }

  /**
   * Get current theme
   */
  getCurrentTheme(): 'dark' | 'light' {
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }
}
